import React, { useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	TextField,
	Alert,
	Paper,
	IconButton,
	Tooltip,
	Link
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNickFetch } from '../hooks/useNickFetch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PsychologyIcon from '@mui/icons-material/Psychology';

type Props = {
	open: boolean;
	onClose: () => void;
	shop_id: number;
	onPromptGenerated?: (prompt: string) => void;
};

type Reference = {
	url: string;
	explanation: string;
};

export default function PromptGeneratorDialog(props: Props) {
	const [articleUrls, setArticleUrls] = useState<string>('');
	const [generatedPrompt, setGeneratedPrompt] = useState<string>('');
	const [references, setReferences] = useState<Reference[]>([]);
	const [generatingPrompt, setGeneratingPrompt] = useState<boolean>(false);
	const [error, setError] = useState<string>('');
	const { nickFetch } = useNickFetch();

	const handleGeneratePrompt = async (useUrls: boolean = false) => {
		setGeneratingPrompt(true);
		setError('');

		try {
			const requestData: any = {
				shop_id: props.shop_id
			};

			// URLが指定されている場合はURLを使用、そうでなければHot Pepperブログから取得
			if (useUrls && articleUrls.trim()) {
				// 複数行のURLを配列に変換（改行で区切る）
				const urls = articleUrls
					.split('\n')
					.map(url => url.trim())
					.filter(url => url.length > 0);
				
				if (urls.length === 0) {
					setError('URLを入力してください');
					setGeneratingPrompt(false);
					return;
				}

				requestData.urls = urls;
			}

			const res = await nickFetch("/generate_blog_prompt", requestData);

			if (!res.error && res.data && res.data.prompt) {
				setGeneratedPrompt(res.data.prompt);
				setReferences(res.data.references || []);
				// プロンプトが生成されただけでは親コンポーネントに通知しない
				// 「このプロンプトを使用」ボタンを押したときにのみ通知する
			} else {
				setError('プロンプト生成に失敗しました');
			}
		} catch (error) {
			setError('プロンプト生成に失敗しました');
		} finally {
			setGeneratingPrompt(false);
		}
	};

	const handleCopy = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text);
		} catch (err) {
			// フォールバック: 古いブラウザ対応
			const textArea = document.createElement('textarea');
			textArea.value = text;
			document.body.appendChild(textArea);
			textArea.select();
			document.execCommand('copy');
			document.body.removeChild(textArea);
		}
	};

	const handleClose = () => {
		setArticleUrls('');
		setGeneratedPrompt('');
		setReferences([]);
		setError('');
		setGeneratingPrompt(false);
		props.onClose();
	};

	return (
		<Dialog
			open={props.open}
			onClose={handleClose}
			fullWidth
			maxWidth="md"
			PaperProps={{
				sx: {
					maxHeight: '90vh'
				}
			}}
		>
			<DialogTitle>
				ブログURLからAIプロンプト生成
			</DialogTitle>
			<DialogContent sx={{ overflowY: 'auto' }}>
				<Box py={2}>
					{/* URL入力欄 */}
					<Box mb={3}>
						<Typography variant="h6" mb={2}>
							1. ブログURLを入力
						</Typography>
						<TextField
							fullWidth
							multiline
							rows={4}
							label="ブログURL"
							placeholder="https://beauty.hotpepper.jp/slnH000000000/blog/&#10;https://beauty.hotpepper.jp/kr/slnH000000000/blog/"
							value={articleUrls}
							onChange={(e) => setArticleUrls(e.target.value)}
							helperText="複数の記事URLを改行で区切って入力できます（最大10件）記事一覧ページの解析も可能です。"
						/>
					</Box>

					{/* プロンプト生成ボタン */}
					<Box mb={3}>
						<Typography variant="h6" mb={2}>
							2. プロンプトを生成
						</Typography>
						<LoadingButton
							variant="contained"
							size="large"
							startIcon={<PsychologyIcon />}
							onClick={() => handleGeneratePrompt(true)}
							loading={generatingPrompt}
							disabled={!articleUrls.trim()}
							fullWidth
						>
							URLから生成
						</LoadingButton>
						{generatingPrompt && (
							<Box mt={2}>
								<Alert severity="info">
									<Typography variant="body2">
										プロンプトの生成には時間がかかります（30秒〜1分程度）<br />
										しばらくお待ちください...
									</Typography>
								</Alert>
							</Box>
						)}
					</Box>

					{/* エラー表示 */}
					{error && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{error}
						</Alert>
					)}

					{/* 生成結果 */}
					{generatedPrompt && (
						<Box>
							<Typography variant="h6" mb={2}>
								3. 生成されたプロンプト
							</Typography>

							{/* 参考にした記事 */}
							{references.length > 0 && (
								<Box mb={3}>
									<Typography variant="subtitle2" mb={1.5}>
										参考にした記事
									</Typography>
									<Paper elevation={1} sx={{ p: 2 }}>
										{references.map((ref, index) => (
											<Box key={index} mb={index < references.length - 1 ? 2 : 0}>
												<Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
													{index + 1}.{' '}
													{ref.url ? (
														<Link
															href={ref.url}
															target="_blank"
															rel="noopener noreferrer"
															sx={{
																color: 'primary.main',
																textDecoration: 'none',
																'&:hover': {
																	textDecoration: 'underline'
																}
															}}
														>
															{ref.url}
														</Link>
													) : (
														'URL不明'
													)}
												</Typography>
												<Typography variant="body2" color="text.secondary" mb={1}>
													{ref.explanation || '参考にしました'}
												</Typography>
												{index < references.length - 1 && <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mt: 1 }} />}
											</Box>
										))}
									</Paper>
								</Box>
							)}

							<Paper elevation={1} sx={{ p: 2 }}>
								<Box display="flex" alignItems="center" mb={1}>
									<Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
										プロンプト
									</Typography>
									<Tooltip title="プロンプトをコピー">
										<IconButton 
											onClick={() => handleCopy(generatedPrompt)}
											size="small"
										>
											<ContentCopyIcon />
										</IconButton>
									</Tooltip>
								</Box>
								<Box
									component="textarea"
									value={generatedPrompt}
									onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setGeneratedPrompt(e.target.value)}
									placeholder="生成されたプロンプトがここに表示されます"
									sx={{
										width: '100%',
										minHeight: '400px',
										padding: '16.5px 14px',
										fontFamily: 'monospace',
										fontSize: '0.875rem',
										lineHeight: '1.6',
										whiteSpace: 'pre-wrap',
										wordBreak: 'break-word',
										border: '1px solid rgba(0, 0, 0, 0.23)',
										borderRadius: '4px',
										resize: 'vertical',
										overflow: 'auto',
										backgroundColor: 'background.paper',
										color: 'text.primary',
										'&:focus': {
											outline: 'none',
											border: '2px solid #1976d2'
										},
										'&:hover': {
											borderColor: 'text.primary'
										},
										'&::placeholder': {
											color: 'text.secondary',
											opacity: 0.42
										}
									}}
								/>
								<Typography variant="caption" color="text.secondary" display="block" mt={1}>
									このプロンプトを編集することもできます。編集後、「このプロンプトを使用」ボタンでAIブログ生成に使用できます。
								</Typography>
							</Paper>
						</Box>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>
					閉じる
				</Button>
				{generatedPrompt && (
					<Button 
						variant="contained"
						onClick={() => {
							if (props.onPromptGenerated) {
								props.onPromptGenerated(generatedPrompt);
							}
							handleClose();
						}}
					>
						このプロンプトを使用
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}
